"use client";

import _toConsumableArray from "/codebuild/output/src3481540116/src/actions-runner/_work/www-lwb-org-au/www-lwb-org-au/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/codebuild/output/src3481540116/src/actions-runner/_work/www-lwb-org-au/www-lwb-org-au/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { getElementPolygon, getEventPoint, isPointInPolygon } from "./X7QOZUD3.js";
import { usePopover } from "./T6C2RYFI.js";
import { createDialogComponent } from "./JC64G2H7.js";
import { HovercardScopedContextProvider, useHovercardProviderContext } from "./EM5CXX6A.js";
import { createElement, createHook, forwardRef } from "./LMDWO4NN.js";
import { useBooleanEvent, useEvent, useIsMouseMoving, useLiveRef, useMergeRefs, usePortalRef, useSafeLayoutEffect, useWrapElement } from "./ABQUS43J.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/hovercard/hovercard.tsx
import { contains } from "@ariakit/core/utils/dom";
import { addGlobalEventListener } from "@ariakit/core/utils/events";
import { hasFocusWithin } from "@ariakit/core/utils/focus";
import { chain, invariant, isFalsyBooleanCallback } from "@ariakit/core/utils/misc";
import { sync } from "@ariakit/core/utils/store";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "div";
function isMovingOnHovercard(target, card, anchor, nested) {
  if (hasFocusWithin(card)) return true;
  if (!target) return false;
  if (contains(card, target)) return true;
  if (anchor && contains(anchor, target)) return true;
  if (nested == null ? void 0 : nested.some(function (card2) {
    return isMovingOnHovercard(target, card2, anchor);
  })) {
    return true;
  }
  return false;
}
function useAutoFocusOnHide(_a) {
  var _b = _a,
    store = _b.store,
    props = __objRest(_b, ["store"]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    autoFocusOnHide = _useState2[0],
    setAutoFocusOnHide = _useState2[1];
  var mounted = store.useState("mounted");
  useEffect(function () {
    if (!mounted) {
      setAutoFocusOnHide(false);
    }
  }, [mounted]);
  var onFocusProp = props.onFocus;
  var onFocus = useEvent(function (event) {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    setAutoFocusOnHide(true);
  });
  var finalFocusRef = useRef(null);
  useEffect(function () {
    return sync(store, ["anchorElement"], function (state) {
      finalFocusRef.current = state.anchorElement;
    });
  }, []);
  props = __spreadProps(__spreadValues({
    autoFocusOnHide: autoFocusOnHide,
    finalFocus: finalFocusRef
  }, props), {
    onFocus: onFocus
  });
  return props;
}
var NestedHovercardContext = createContext(null);
var useHovercard = createHook(function useHovercard2(_a) {
  var _b = _a,
    store = _b.store,
    _b$modal = _b.modal,
    modal = _b$modal === void 0 ? false : _b$modal,
    _b$portal = _b.portal,
    portal = _b$portal === void 0 ? !!modal : _b$portal,
    _b$hideOnEscape = _b.hideOnEscape,
    _hideOnEscape = _b$hideOnEscape === void 0 ? true : _b$hideOnEscape,
    _b$hideOnHoverOutside = _b.hideOnHoverOutside,
    hideOnHoverOutside = _b$hideOnHoverOutside === void 0 ? true : _b$hideOnHoverOutside,
    _b$disablePointerEven = _b.disablePointerEventsOnApproach,
    disablePointerEventsOnApproach = _b$disablePointerEven === void 0 ? !!hideOnHoverOutside : _b$disablePointerEven,
    props = __objRest(_b, ["store", "modal", "portal", "hideOnEscape", "hideOnHoverOutside", "disablePointerEventsOnApproach"]);
  var context = useHovercardProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "Hovercard must receive a `store` prop or be wrapped in a HovercardProvider component.");
  var ref = useRef(null);
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    nestedHovercards = _useState4[0],
    setNestedHovercards = _useState4[1];
  var hideTimeoutRef = useRef(0);
  var enterPointRef = useRef(null);
  var _usePortalRef = usePortalRef(portal, props.portalRef),
    portalRef = _usePortalRef.portalRef,
    domReady = _usePortalRef.domReady;
  var isMouseMoving = useIsMouseMoving();
  var mayHideOnHoverOutside = !!hideOnHoverOutside;
  var hideOnHoverOutsideProp = useBooleanEvent(hideOnHoverOutside);
  var mayDisablePointerEvents = !!disablePointerEventsOnApproach;
  var disablePointerEventsProp = useBooleanEvent(disablePointerEventsOnApproach);
  var open = store.useState("open");
  var mounted = store.useState("mounted");
  useEffect(function () {
    if (!domReady) return;
    if (!mounted) return;
    if (!mayHideOnHoverOutside && !mayDisablePointerEvents) return;
    var element = ref.current;
    if (!element) return;
    var onMouseMove = function onMouseMove(event) {
      if (!store) return;
      if (!isMouseMoving()) return;
      var _store$getState = store.getState(),
        anchorElement = _store$getState.anchorElement,
        hideTimeout = _store$getState.hideTimeout,
        timeout = _store$getState.timeout;
      var enterPoint = enterPointRef.current;
      var _event$composedPath = event.composedPath(),
        _event$composedPath2 = _slicedToArray(_event$composedPath, 1),
        target = _event$composedPath2[0];
      var anchor = anchorElement;
      if (isMovingOnHovercard(target, element, anchor, nestedHovercards)) {
        enterPointRef.current = target && anchor && contains(anchor, target) ? getEventPoint(event) : null;
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = 0;
        return;
      }
      if (hideTimeoutRef.current) return;
      if (enterPoint) {
        var currentPoint = getEventPoint(event);
        var polygon = getElementPolygon(element, enterPoint);
        if (isPointInPolygon(currentPoint, polygon)) {
          enterPointRef.current = currentPoint;
          if (!disablePointerEventsProp(event)) return;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }
      if (!hideOnHoverOutsideProp(event)) return;
      hideTimeoutRef.current = window.setTimeout(function () {
        hideTimeoutRef.current = 0;
        store == null ? void 0 : store.hide();
      }, hideTimeout != null ? hideTimeout : timeout);
    };
    return chain(addGlobalEventListener("mousemove", onMouseMove, true), function () {
      return clearTimeout(hideTimeoutRef.current);
    });
  }, [store, isMouseMoving, domReady, mounted, mayHideOnHoverOutside, mayDisablePointerEvents, nestedHovercards, disablePointerEventsProp, hideOnHoverOutsideProp]);
  useEffect(function () {
    if (!domReady) return;
    if (!mounted) return;
    if (!mayDisablePointerEvents) return;
    var disableEvent = function disableEvent(event) {
      var element = ref.current;
      if (!element) return;
      var enterPoint = enterPointRef.current;
      if (!enterPoint) return;
      var polygon = getElementPolygon(element, enterPoint);
      if (isPointInPolygon(getEventPoint(event), polygon)) {
        if (!disablePointerEventsProp(event)) return;
        event.preventDefault();
        event.stopPropagation();
      }
    };
    return chain(
    // Note: we may need to add pointer events here in the future.
    addGlobalEventListener("mouseenter", disableEvent, true), addGlobalEventListener("mouseover", disableEvent, true), addGlobalEventListener("mouseout", disableEvent, true), addGlobalEventListener("mouseleave", disableEvent, true));
  }, [domReady, mounted, mayDisablePointerEvents, disablePointerEventsProp]);
  useEffect(function () {
    if (!domReady) return;
    if (open) return;
    store == null ? void 0 : store.setAutoFocusOnShow(false);
  }, [store, domReady, open]);
  var openRef = useLiveRef(open);
  useEffect(function () {
    if (!domReady) return;
    return function () {
      if (!openRef.current) {
        store == null ? void 0 : store.setAutoFocusOnShow(false);
      }
    };
  }, [store, domReady]);
  var registerOnParent = useContext(NestedHovercardContext);
  useSafeLayoutEffect(function () {
    if (modal) return;
    if (!portal) return;
    if (!mounted) return;
    if (!domReady) return;
    var element = ref.current;
    if (!element) return;
    return registerOnParent == null ? void 0 : registerOnParent(element);
  }, [modal, portal, mounted, domReady]);
  var registerNestedHovercard = useCallback(function (element) {
    setNestedHovercards(function (prevElements) {
      return [].concat(_toConsumableArray(prevElements), [element]);
    });
    var parentUnregister = registerOnParent == null ? void 0 : registerOnParent(element);
    return function () {
      setNestedHovercards(function (prevElements) {
        return prevElements.filter(function (item) {
          return item !== element;
        });
      });
      parentUnregister == null ? void 0 : parentUnregister();
    };
  }, [registerOnParent]);
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(HovercardScopedContextProvider, {
      value: store,
      children: /* @__PURE__ */jsx(NestedHovercardContext.Provider, {
        value: registerNestedHovercard,
        children: element
      })
    });
  }, [store, registerNestedHovercard]);
  props = __spreadProps(__spreadValues({}, props), {
    ref: useMergeRefs(ref, props.ref)
  });
  props = useAutoFocusOnHide(__spreadValues({
    store: store
  }, props));
  var autoFocusOnShow = store.useState(function (state) {
    return modal || state.autoFocusOnShow;
  });
  props = usePopover(__spreadProps(__spreadValues({
    store: store,
    modal: modal,
    portal: portal,
    autoFocusOnShow: autoFocusOnShow
  }, props), {
    portalRef: portalRef,
    hideOnEscape: function hideOnEscape(event) {
      if (isFalsyBooleanCallback(_hideOnEscape, event)) return false;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          store == null ? void 0 : store.hide();
        });
      });
      return true;
    }
  }));
  return props;
});
var Hovercard = createDialogComponent(forwardRef(function Hovercard2(props) {
  var htmlProps = useHovercard(props);
  return createElement(TagName, htmlProps);
}), useHovercardProviderContext);
export { useHovercard, Hovercard };